import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthProvider";
import FirstTimeUserModal from "./FirstTimeUserModal";
import unitModel from "../lib/firebase/unitModel";
import { listTenants } from "../lib/firebase/tenantModel";
import userModel from "../lib/firebase/userModel";
import { noFirstTimeModalPaths } from "../helpers/constants";
import { useConnectStatus } from "../hooks/useConnectStatus";

const FirstTimeUserModalWrapper = () => {
  const { user, userInfo, isUserInfoLoading, isOpsChatOnlyUser } = useContext(AuthContext);
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const hasCheckedForUserRef = useRef(null); // Track which user we've checked for (using ref to avoid re-renders)
  const taskCheckInFlightRef = useRef(false);

  // Check Connect status for real-time payment capability
  const { canAcceptPayments, loading: connectStatusLoading } = useConnectStatus(
    user?.uid
  );

  const canAcceptPaymentsRef = useRef(canAcceptPayments);
  const userInfoRef = useRef(userInfo);
  const showModalRef = useRef(showModal);
  canAcceptPaymentsRef.current = canAcceptPayments;
  userInfoRef.current = userInfo;
  showModalRef.current = showModal;

  // Check if current route should exclude the first-time modal
  const shouldExcludeModal = noFirstTimeModalPaths.some((path) => {
    // Handle root path exactly
    if (path === "/") {
      return location.pathname === "/";
    }
    // Handle dynamic routes like /communication/:tenantId
    if (path.includes(":")) {
      const basePath = path.split(":")[0];
      return location.pathname.startsWith(basePath);
    }
    // For other paths, check if pathname starts with the path (to handle exact matches)
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  });

  useEffect(() => {
    const devLog = (...args) => {
      if (process.env.NODE_ENV === "development") {
        console.log(...args);
      }
    };

    // Don't show modal on excluded routes
    if (shouldExcludeModal) {
      devLog(
        "[FirstTimeUserModalWrapper] Skipping - route excluded from first-time modal"
      );
      return;
    }

    // OpsChat-only users have their own onboarding modal (OpsChatFirstTimeModalWrapper)
    if (isOpsChatOnlyUser) {
      devLog(
        "[FirstTimeUserModalWrapper] Skipping - OpsChat-only user"
      );
      return;
    }

    // Only check once user info is loaded
    if (isUserInfoLoading) {
      devLog(
        "[FirstTimeUserModalWrapper] Skipping - still loading user info"
      );
      return;
    }

    // Wait for Connect status to load before checking tasks
    if (connectStatusLoading) {
      devLog(
        "[FirstTimeUserModalWrapper] Skipping - still loading Connect status"
      );
      return;
    }

    // Only show if user is logged in
    if (!user) {
      devLog("[FirstTimeUserModalWrapper] No user, skipping");
      hasCheckedForUserRef.current = null;
      return;
    }

    // If we've already checked for this specific user, only re-check if modal is currently open
    // This allows us to close the modal if all tasks become complete while it's open
    if (hasCheckedForUserRef.current === user.uid && !showModalRef.current) {
      return;
    }

    // Check if user has seen the modal before (in Firebase)
    // If the property doesn't exist, treat it as false (they haven't seen it)
    const hasSeenModalInFirebase =
      userInfoRef.current?.hasSeenFirstTimeModal === true;

    // Check if we've shown it in this browser session (to avoid showing on every page refresh)
    const sessionKey = `firstTimeModalShown_${user.uid}`;
    const hasSeenModalInSession = sessionStorage.getItem(sessionKey) === "true";

    // Check if all tasks are completed
    let timeoutId = null;
    const checkAllTasksCompleted = async () => {
      if (taskCheckInFlightRef.current) return;
      taskCheckInFlightRef.current = true;
      try {
        // Check bank account setup - use canAcceptPayments from useConnectStatus
        // This ensures consistency with Settings page and FirstTimeUserModal
        const bankAccountSetup = canAcceptPaymentsRef.current || false;

        // Check if user has properties/units
        const units = await unitModel.getByOwner(user.uid);
        const hasProperty = units && units.length > 0;

        // Check if user has tenants
        const tenantsResult = await listTenants();
        const hasTenant =
          tenantsResult?.tenants && tenantsResult.tenants.length > 0;

        // Only check the three tasks that are actually shown in the modal
        const allTasksCompleted = bankAccountSetup && hasProperty && hasTenant;

        devLog("[FirstTimeUserModalWrapper] Task completion status:", {
          bankAccountSetup,
          hasProperty,
          hasTenant,
          allTasksCompleted,
          showModal: showModalRef.current,
          hasSeenModalInFirebase,
          hasSeenModalInSession,
        });

        // ALWAYS check if all tasks are completed first, regardless of session state
        // This ensures the modal closes even if it was already shown
        if (allTasksCompleted) {
          devLog(
            "[FirstTimeUserModalWrapper] All tasks completed, closing modal and marking as seen"
          );

          // Always close the modal if all tasks are completed
          setShowModal(false);

          // Mark as seen in session
          sessionStorage.setItem(sessionKey, "true");

          // Mark as seen in Firebase if not already marked
          if (!hasSeenModalInFirebase) {
            try {
              await userModel.update({
                id: user.uid,
                data: { hasSeenFirstTimeModal: true },
              });
              devLog(
                "[FirstTimeUserModalWrapper] Marked modal as seen in Firebase"
              );
            } catch (error) {
              console.error(
                "[FirstTimeUserModalWrapper] Error marking modal as seen:",
                error
              );
            }
          }

          return;
        }

        // Only show modal if:
        // 1. Not all tasks are completed (we already checked above)
        // 2. User hasn't seen the modal in Firebase (hasSeenFirstTimeModal is not true)
        // 3. User hasn't seen the modal in this browser session
        // This ensures:
        // - New users see it on their first login
        // - Existing users who haven't seen it will see it once
        // - It won't show on every page refresh (sessionStorage prevents that)
        // - It won't show if all tasks are already completed
        if (!hasSeenModalInFirebase && !hasSeenModalInSession) {
          devLog(
            "[FirstTimeUserModalWrapper] Conditions met, showing modal in 1 second"
          );

          // Small delay to ensure page is loaded
          timeoutId = setTimeout(() => {
            devLog("[FirstTimeUserModalWrapper] Showing modal now");
            setShowModal(true);
            // Mark as shown in this session
            sessionStorage.setItem(sessionKey, "true");
          }, 1000);
        } else {
          devLog(
            "[FirstTimeUserModalWrapper] User has already seen modal, not showing"
          );
        }
      } catch (error) {
        console.error(
          "[FirstTimeUserModalWrapper] Error checking task status:",
          error
        );
        // If there's an error checking tasks, fall back to the original logic
        if (!hasSeenModalInFirebase && !hasSeenModalInSession) {
          devLog(
            "[FirstTimeUserModalWrapper] Error occurred, showing modal anyway in 1 second"
          );
          timeoutId = setTimeout(() => {
            setShowModal(true);
            sessionStorage.setItem(sessionKey, "true");
          }, 1000);
        }
      } finally {
        taskCheckInFlightRef.current = false;
        hasCheckedForUserRef.current = user.uid;
      }
    };

    checkAllTasksCompleted();

    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    user,
    isUserInfoLoading,
    isOpsChatOnlyUser,
    connectStatusLoading,
    shouldExcludeModal,
    location.pathname,
  ]);

  const handleClose = () => {
    setShowModal(false);
  };

  // Separate effect to continuously monitor task completion and close modal if all tasks complete
  // This acts as a safety net to ensure the modal closes even if the main logic doesn't catch it
  useEffect(() => {
    if (!user || connectStatusLoading || !showModal) return;

    const checkAndCloseIfComplete = async () => {
      try {
        const bankAccountSetup = canAcceptPaymentsRef.current || false;
        const units = await unitModel.getByOwner(user.uid);
        const hasProperty = units && units.length > 0;
        const tenantsResult = await listTenants();
        const hasTenant =
          tenantsResult?.tenants && tenantsResult.tenants.length > 0;
        const allTasksCompleted = bankAccountSetup && hasProperty && hasTenant;

        if (allTasksCompleted && showModal) {
          if (process.env.NODE_ENV === "development") {
            console.log(
              "[FirstTimeUserModalWrapper] Safety check: All tasks completed, closing modal"
            );
          }
          setShowModal(false);
          const sessionKey = `firstTimeModalShown_${user.uid}`;
          sessionStorage.setItem(sessionKey, "true");
        }
      } catch (error) {
        console.error(
          "[FirstTimeUserModalWrapper] Error in safety check:",
          error
        );
      }
    };

    // Check immediately
    checkAndCloseIfComplete();

    // Set up interval to check periodically while modal is open
    const interval = setInterval(() => {
      if (showModal) {
        checkAndCloseIfComplete();
      }
    }, 2000); // Check every 2 seconds while modal is open

    return () => clearInterval(interval);
  }, [user, canAcceptPayments, connectStatusLoading, showModal]);

  // Dev helper: Expose function to manually trigger modal (for testing)
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      window.__triggerFirstTimeModal = () => {
        if (user?.uid) {
          const sessionKey = `firstTimeModalShown_${user.uid}`;
          sessionStorage.removeItem(sessionKey);
          hasCheckedForUserRef.current = null;
          setShowModal(true);
          console.log("[FirstTimeUserModalWrapper] Modal triggered manually");
        } else {
          console.warn("[FirstTimeUserModalWrapper] No user logged in");
        }
      };
    }
  }, [user]);

  return <FirstTimeUserModal isOpen={showModal} onClose={handleClose} />;
};

export default FirstTimeUserModalWrapper;
